import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
  DialogTrigger,
} from "@/taskpane/components/ui/dialog";
import { Button } from "@/taskpane/components/ui/button";
import { Textarea } from "@/taskpane/components/ui/textarea";
import { useTileEdit } from "@/hooks/useTileEdit";
import { ReviewTileItem } from "@/types/tile.types";
import type { EditTileRequest } from "@/types/review.types";
import { useDocument } from "@/hooks/useDocument";
import { toast } from "sonner";
import { getBackendDocumentId, notifyDocumentNotReady } from "@/utils/document-id.utils";

interface EditTileDialogProps {
  tile: ReviewTileItem;
  queryTerm: string;
  children: React.ReactNode;
}

function resolveEditableField(tile: ReviewTileItem): { key: string; text: string } | null {
  if (tile.originalData == null) return null;
  const data = tile.originalData;
  if ("suggestedclause" in data && typeof data.suggestedclause === "string") {
    return { key: "suggestedclause", text: data.suggestedclause };
  }
  if ("suggestion" in data && typeof data.suggestion === "string") {
    return { key: "suggestion", text: data.suggestion };
  }
  if ("suggestions" in data && typeof data.suggestions === "string") {
    return { key: "suggestions", text: data.suggestions };
  }
  return null;
}

export function EditTileDialog({ tile, queryTerm, children }: EditTileDialogProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [editedText, setEditedText] = useState("");
  const [editableKey, setEditableKey] = useState<string>("");
  const { data: document } = useDocument();

  const { mutate: editTile, isPending: isLoading } = useTileEdit();

  useEffect(() => {
    const resolved = resolveEditableField(tile);
    if (resolved) {
      setEditableKey(resolved.key);
      setEditedText(resolved.text);
    }
  }, [tile]);

  const handleSave = () => {
    const documentId = getBackendDocumentId(document);
    if (documentId == null) {
      notifyDocumentNotReady("review");
      return;
    }
    if (editableKey === "") return;

    if (tile.originalData == null) return;

    const payload: EditTileRequest = {
      document_id: [documentId],
      query_term: queryTerm,
      tile_id: tile.originalData.tile_id,
      tile: {
        ...tile.originalData,
        [editableKey]: editedText,
      },
    };
    editTile(payload, {
      onSuccess: () => {
        setIsOpen(false);
        toast.success("Tile updated", { duration: 2000 });
      },
      onError: (error) => {
        console.error("Error updating tile:", error);
        toast.error("Failed to update tile", { duration: 2000 });
      },
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit Tile</DialogTitle>
        </DialogHeader>
        <Textarea
          value={editedText}
          onChange={(e) => setEditedText(e.target.value)}
          rows={10}
          disabled={isLoading}
        />
        <DialogFooter className="flex-row justify-end gap-2">
          <DialogClose asChild>
            <Button variant="outline" disabled={isLoading}>
              Cancel
            </Button>
          </DialogClose>
          <Button onClick={handleSave} disabled={isLoading}>
            {isLoading ? "Saving..." : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
